/**
 * Freshness Checking - Unified API for data freshness and degradation
 *
 * SYNC: This module is shared between frontend and backend.
 */

import { FRESHNESS, DISPLAY_AGE, VALIDATION } from './constants.mjs';
import { parseAny } from './parse.mjs';

/**
 * @typedef {'fresh' | 'stale' | 'expired' | 'unavailable' | 'unknown'} FreshnessStatus
 */

/**
 * @typedef {Object} FreshnessResult
 * @property {FreshnessStatus} status - Freshness status
 * @property {number|null} age - Age in milliseconds
 * @property {number|null} ageMinutes - Age in minutes (rounded)
 * @property {Date|null} observedAt - Parsed observation time
 */

/**
 * Check data freshness against thresholds
 *
 * @param {Date|string|number} observedAt - When data was observed
 * @param {'buoy' | 'tide' | 'weather' | 'sun'} dataType - Type of data
 * @param {Date} relativeTo - Reference time (default: now)
 * @returns {FreshnessResult}
 */
export function checkFreshness(observedAt, dataType = 'buoy', relativeTo = new Date()) {
    const observed = parseAny(observedAt);

    if (!observed) {
        return {
            status: 'unknown',
            age: null,
            ageMinutes: null,
            observedAt: null,
        };
    }

    const thresholds = FRESHNESS[dataType] || FRESHNESS.buoy;
    const age = relativeTo - observed;

    let status;
    if (age <= thresholds.fresh) {
        status = 'fresh';
    } else if (age <= thresholds.stale) {
        status = 'stale';
    } else if (age <= thresholds.expired) {
        status = 'expired';
    } else {
        status = 'unavailable';
    }

    return {
        status,
        age,
        ageMinutes: Math.round(age / 60000),
        observedAt: observed,
    };
}

/**
 * Get display class and text for data age (UI freshness indicators)
 *
 * @param {Date|string|number} timestamp - Data timestamp
 * @returns {{ text: string, class: 'fresh' | 'stale' | 'old' }}
 */
export function getDisplayAge(timestamp) {
    const date = parseAny(timestamp);

    if (!date) {
        return { text: 'Unknown', class: 'old' };
    }

    const ageMs = Date.now() - date;
    const mins = Math.floor(ageMs / 60000);

    if (mins < 1) {
        return { text: 'Just now', class: 'fresh' };
    }

    if (ageMs < DISPLAY_AGE.fresh) {
        return { text: `${mins}m ago`, class: 'fresh' };
    }

    if (ageMs < DISPLAY_AGE.stale) {
        return { text: `${mins}m ago`, class: 'stale' };
    }

    if (mins < 60) {
        return { text: `${mins}m ago`, class: 'old' };
    }

    return { text: `${Math.floor(mins / 60)}h ago`, class: 'old' };
}

/**
 * Check if timestamp is in the future (with grace period for clock drift)
 *
 * @param {Date|string|number} timestamp - Timestamp to check
 * @param {number} graceMs - Grace period in ms (default: from VALIDATION)
 * @returns {boolean}
 */
export function isFutureTimestamp(timestamp, graceMs = VALIDATION.futureThreshold) {
    const date = parseAny(timestamp);
    if (!date) return false;

    return date > new Date(Date.now() + graceMs);
}

/**
 * Simple staleness check (for monitoring/canary)
 *
 * @param {Date|string|number} timestamp - Timestamp to check
 * @param {number} thresholdMinutes - Stale threshold in minutes
 * @returns {boolean}
 */
export function isStale(timestamp, thresholdMinutes) {
    const date = parseAny(timestamp);
    if (!date) return true;

    const ageMs = Date.now() - date;
    return ageMs > thresholdMinutes * 60 * 1000;
}

/**
 * Calculate degradation level from multiple data sources
 *
 * Level 0 (Live):        Primary sources fresh, all data OK, observation data
 * Level 1 (Fallback):    Using fallback source OR stale primary OR forecast data
 * Level 2 (Stale):       Data expired
 * Level 3 (Unavailable): Data unavailable OR critical sources missing
 *
 * @param {Object} dataStatus - Status of each data source
 * @param {FreshnessResult} [dataStatus.buoyFreshness]
 * @param {FreshnessResult} [dataStatus.tideFreshness]
 * @param {FreshnessResult} [dataStatus.weatherFreshness]
 * @param {boolean} [dataStatus.usingFallback]
 * @param {'observation' | 'forecast'} [dataStatus.buoyDataType]
 * @param {string[]} [dataStatus.missingCritical]
 * @returns {{ level: 0|1|2|3, reason: string|null }}
 */
export function calculateDegradation(dataStatus) {
    const {
        buoyFreshness,
        tideFreshness,
        weatherFreshness,
        usingFallback = false,
        buoyDataType = 'observation',
        missingCritical = [],
    } = dataStatus;

    // Level 3: Missing critical data
    if (missingCritical.length > 0) {
        return {
            level: 3,
            reason: `Missing critical data: ${missingCritical.join(', ')}`,
        };
    }

    const statuses = [
        { type: 'buoy', freshness: buoyFreshness },
        { type: 'tide', freshness: tideFreshness },
        { type: 'weather', freshness: weatherFreshness },
    ].filter(s => s.freshness);

    // Any unavailable = Level 3
    const unavailable = statuses.filter(s => s.freshness.status === 'unavailable');
    if (unavailable.length > 0) {
        return {
            level: 3,
            reason: `Data unavailable: ${unavailable.map(s => s.type).join(', ')}`,
        };
    }

    // Any expired = Level 2
    const expired = statuses.filter(s => s.freshness.status === 'expired');
    if (expired.length > 0) {
        return {
            level: 2,
            reason: `Data expired: ${expired.map(s => `${s.type} (${s.freshness.ageMinutes}min)`).join(', ')}`,
        };
    }

    // Using forecast buoy data = Level 1
    if (buoyDataType === 'forecast') {
        return {
            level: 1,
            reason: 'Using wave model forecast (buoy observation unavailable)',
        };
    }

    // Any stale or using fallback = Level 1
    const stale = statuses.filter(s => s.freshness.status === 'stale');
    if (stale.length > 0 || usingFallback) {
        const reasons = [];
        if (stale.length > 0) {
            reasons.push(`Stale: ${stale.map(s => s.type).join(', ')}`);
        }
        if (usingFallback) {
            reasons.push('Using fallback source');
        }
        return {
            level: 1,
            reason: reasons.join('. '),
        };
    }

    // All fresh = Level 0
    return { level: 0, reason: null };
}

/**
 * Detect stuck sensor (same value repeated)
 *
 * @param {number} currentValue - Current reading
 * @param {number[]} previousValues - Previous readings
 * @param {number} threshold - Number of identical readings to trigger (default: from VALIDATION)
 * @returns {{ isStuck: boolean, reason?: string }}
 */
export function detectStuckSensor(currentValue, previousValues, threshold = VALIDATION.stuckSensorReadings) {
    if (!previousValues || previousValues.length < threshold) {
        return { isStuck: false };
    }

    // Check if all recent values are exactly the same
    const recentValues = previousValues.slice(-threshold);
    const allSame = recentValues.every(v => v === currentValue);

    if (allSame) {
        return {
            isStuck: true,
            reason: `Value stuck at ${currentValue} for ${threshold} consecutive readings`,
        };
    }

    return { isStuck: false };
}

/**
 * Validate temporal aspects of a reading
 *
 * @param {Object} data - Data with observedAt field
 * @param {'buoy' | 'tide' | 'weather' | 'sun'} dataType
 * @returns {{ status: 'valid' | 'suspect' | 'rejected', flags: Array, freshness: FreshnessResult, data: Object }}
 */
export function validateTemporal(data, dataType = 'buoy') {
    const flags = [];
    let status = 'valid';

    // Check freshness
    const freshness = checkFreshness(data.observedAt, dataType);

    if (freshness.status === 'unavailable') {
        status = 'rejected';
        flags.push({
            type: 'freshness',
            reason: `Data too old: ${freshness.ageMinutes} minutes`,
            ageMinutes: freshness.ageMinutes,
        });
    } else if (freshness.status === 'expired') {
        if (status === 'valid') status = 'suspect';
        flags.push({
            type: 'freshness',
            reason: `Data expired: ${freshness.ageMinutes} minutes old`,
            ageMinutes: freshness.ageMinutes,
        });
    } else if (freshness.status === 'stale') {
        if (status === 'valid') status = 'suspect';
        flags.push({
            type: 'freshness',
            reason: `Data stale: ${freshness.ageMinutes} minutes old`,
            ageMinutes: freshness.ageMinutes,
        });
    }

    // Check for future timestamps
    if (data.observedAt && isFutureTimestamp(data.observedAt)) {
        status = 'rejected';
        const observed = parseAny(data.observedAt);
        flags.push({
            type: 'temporal',
            reason: `Future timestamp detected: ${observed?.toISOString()}`,
        });
    }

    return {
        status,
        flags,
        freshness,
        data,
    };
}

/**
 * Build data sources object for tracking
 * Includes dataType to distinguish observation vs forecast data
 *
 * @param {Object} sources - Map of source name to source data
 * @returns {Object}
 */
export function buildDataSources(sources) {
    const result = {};

    for (const [key, source] of Object.entries(sources)) {
        if (source && !source.error) {
            // Handle different field names for observedAt (tide uses waterLevelObservedAt)
            const observedAt = source.observedAt || source.waterLevelObservedAt;
            const date = parseAny(observedAt);

            result[key] = {
                source: source.source || 'unknown',
                dataType: source.dataType || 'observation',
                observedAt: date?.toISOString() || null,
                responseTime: source.responseTime || null,
                fallbackUsed: source.fallbackUsed || false,
            };
        } else {
            result[key] = {
                error: source?.error || 'No data',
            };
        }
    }

    return result;
}
